import MealsApi from "@/packages/api/Api/V1/MealsApi";
import { jsonHeaders } from "@/packages/dashboard/csrf";
import { appendQueryParams, withAppDomain } from "@/packages/dashboard/utils/url-utils";
import { zRailsNumericString } from "@/packages/dashboard/api/z-rails-numeric";
import { z } from "zod";

// Zod schema for food macros
export const foodMacrosSchema = z.object({
  calories: z.number(),
  proteins: zRailsNumericString,
  carbs: zRailsNumericString,
  fats: zRailsNumericString,
  serving_size: zRailsNumericString,
});

// Zod schema for a food item
export const foodItemSchema = foodMacrosSchema.extend({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable().optional(),
  serving_description: z.string().nullable().optional(),
  serving_unit: z.string(),
});

// Zod schema for a meal item
export const mealItemSchema = z.object({
  id: z.number(),
  food_item_id: z.number(),
  quantity: z.string(),
  food_item: foodItemSchema.nullable(),
});

// Zod schema for a meal
export const mealSchema = z.object({
  id: z.number(),
  timestamp: z.string().datetime(),
  meal_items: z.array(mealItemSchema),
});

// Zod schema for the API response
const mealsResponseSchema = z.object({
  meals: z.array(mealSchema),
});

export type MealsResponse = z.infer<typeof mealsResponseSchema>;
export type Meal = z.infer<typeof mealSchema>;
export type MealItem = z.infer<typeof mealItemSchema>;
export type FoodItem = z.infer<typeof foodItemSchema>;
export type FoodMacros = z.infer<typeof foodMacrosSchema>;

type GetMealsParams = {
  start_timestamp: string;
  end_timestamp: string;
};

export async function getMeals(params: GetMealsParams): Promise<MealsResponse> {
  const url = appendQueryParams(withAppDomain(MealsApi.index.path()), params);
  const response = await fetch(url, {
    headers: jsonHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch meals: ${response.statusText}`);
  }

  const data = await response.json();
  return mealsResponseSchema.parse(data);
}
